<template>
	<!-- Lives inside ZoomablePhoto's v-img, so it zooms/pans with the photo for free. The inner box is
	     sized to the photo's CONTAINED rect (container query units + natural aspect), so a click and
	     the outlines are both relative to the photo itself — never to the letterbox bars. -->
	<div v-if="target && naturalWidth > 0 && naturalHeight > 0" class="snapshot-measure-layer">
		<div
			v-test="'snapshot-measure-layer'"
			class="snapshot-measure-layer__photo"
			:style="{ '--photo-ratio': naturalWidth / naturalHeight }"
			@pointerdown="onPointerDown"
			@dblclick.stop
			@click="onClick">
			<svg class="snapshot-measure-layer__outline" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
				<g v-for="outline in outlines" :key="outline.id">
					<polygon
						:points="outline.points"
						stroke="rgba(255, 255, 255, 0.85)"
						:stroke-width="outline.selected ? 5 : 3"
						vector-effect="non-scaling-stroke" />
					<polygon
						:points="outline.points"
						:stroke="outline.selected ? 'rgb(var(--v-theme-primary))' : 'rgba(0, 0, 0, 0.45)'"
						:stroke-width="outline.selected ? 2.5 : 1.5"
						vector-effect="non-scaling-stroke" />
				</g>
			</svg>
			<button
				v-for="outline in outlines"
				:key="`badge-${outline.id}`"
				v-test="'snapshot-measure-badge'"
				type="button"
				class="snapshot-measure-layer__badge"
				:class="{ 'snapshot-measure-layer__badge--selected': outline.selected }"
				:style="{ left: `${outline.badgeX * 100}%`, top: `${outline.badgeY * 100}%` }"
				:aria-label="`Measurement ${outline.number}`"
				@pointerdown.stop
				@click.stop="select(outline.id)">
				{{ outline.number }}
			</button>
			<v-progress-circular
				v-if="pendingTap"
				v-test="'snapshot-measure-progress'"
				class="snapshot-measure-layer__progress"
				:style="{ left: `${pendingTap.xNormalized * 100}%`, top: `${pendingTap.yNormalized * 100}%` }"
				indeterminate
				color="white"
				size="28"
				width="3" />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";

	import { measurementContour, type SnapshotMeasure } from "@/composables/useSnapshotMeasure";

	const props = defineProps<{ measure: SnapshotMeasure; naturalWidth: number; naturalHeight: number }>();
	const { target, measurements, pendingTap, selectedId, tap, select } = props.measure;

	/** Past this many CSS px between press and release, the gesture was a pan, not a click. */
	const DRAG_THRESHOLD_PIXELS = 5;
	let pressedAt: { x: number; y: number } | null = null;

	const outlines = computed(() =>
		measurements.value.flatMap((measurement, index) => {
			const points = (measurementContour(measurement) ?? [])
				.filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]))
				.map((point) => [point[0], point[1]] as const);
			if (points.length < 2) return [];
			const top = points.reduce((highest, point) => (point[1] < highest[1] ? point : highest), points[0]);
			return [
				{
					id: measurement.id,
					number: index + 1,
					selected: measurement.id === selectedId.value,
					points: points.map(([x, y]) => `${x},${y}`).join(" "),
					badgeX: top[0],
					badgeY: top[1]
				}
			];
		})
	);

	// ZoomablePhoto pans on pointerdown; that is left alone so a zoomed photo can still be dragged.
	function onPointerDown(event: PointerEvent): void {
		pressedAt = { x: event.clientX, y: event.clientY };
	}

	// The bounding rect already includes the zoom transform, so this is the photo-relative fraction
	// the object-crop endpoint expects at any zoom level.
	function onClick(event: MouseEvent): void {
		const moved = pressedAt ? Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) : 0;
		pressedAt = null;
		if (moved > DRAG_THRESHOLD_PIXELS) return;
		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
		if (rect.width <= 0 || rect.height <= 0) return;
		void tap((event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height);
	}
</script>

<style scoped>
	.snapshot-measure-layer {
		position: absolute;
		inset: 0;
		container-type: size;
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: none;
	}
	.snapshot-measure-layer__photo {
		position: relative;
		width: min(100cqw, calc(100cqh * var(--photo-ratio)));
		height: min(100cqh, calc(100cqw / var(--photo-ratio)));
		pointer-events: auto;
		cursor: crosshair;
	}
	.snapshot-measure-layer__outline {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		fill: none;
		overflow: visible;
		pointer-events: none;
	}
	.snapshot-measure-layer__badge {
		position: absolute;
		transform: translate(-50%, -120%);
		min-width: 24px;
		height: 24px;
		padding: 0 6px;
		border-radius: 12px;
		background: rgba(0, 0, 0, 0.65);
		color: #fff;
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
	}
	.snapshot-measure-layer__badge--selected {
		background: rgb(var(--v-theme-primary));
	}
	.snapshot-measure-layer__progress {
		position: absolute;
		transform: translate(-50%, -50%);
	}
</style>
